import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionManagementSection = ({ userGender, subscriptionData }) => {
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);

  if (userGender !== 'male') {
    return null;
  }

  const subscription = subscriptionData || {
    plan: 'Premium Monthly',
    status: 'active',
    amount: 2500,
    currency: '₹',
    startDate: '2026-01-28',
    renewalDate: '2026-02-28',
    autoRenew: true,
    paymentMethod: 'UPI',
    transactionId: 'TXN123456789'
  };

  const [invoices] = useState([
    {
      id: 1,
      date: '2026-01-28',
      amount: 2500,
      status: 'paid',
      invoiceNumber: 'INV-2026-001'
    },
    {
      id: 2,
      date: '2025-12-28',
      amount: 2500,
      status: 'paid',
      invoiceNumber: 'INV-2025-012'
    },
    {
      id: 3,
      date: '2025-11-28',
      amount: 2500,
      status: 'paid',
      invoiceNumber: 'INV-2025-011'
    }
  ]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleCancelSubscription = () => {
    alert('Subscription cancelled. You will have access until ' + formatDate(subscription?.renewalDate));
    setShowCancelModal(false);
  };

  const handleDownloadInvoice = (invoiceNumber) => {
    alert(`Downloading invoice ${invoiceNumber}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-semibold">Subscription Management</h3>
          <p className="text-sm text-muted-foreground mt-1">Manage your premium subscription</p>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
          subscription?.status === 'active' ?'bg-success/10 text-success' :'bg-error/10 text-error'
        }`}>
          {subscription?.status === 'active' ? 'Active' : 'Inactive'}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg border border-primary/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-card rounded-lg">
              <Icon name="Crown" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h4 className="text-sm font-caption font-semibold">Current Plan</h4>
              <p className="text-lg font-heading font-bold text-primary">{subscription?.plan}</p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Amount</span>
              <span className="font-mono font-semibold">{subscription?.currency}{subscription?.amount?.toLocaleString('en-IN')}/month</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Started on</span>
              <span className="font-mono">{formatDate(subscription?.startDate)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Renews on</span>
              <span className="font-mono">{formatDate(subscription?.renewalDate)}</span>
            </div>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <h4 className="text-sm font-caption font-semibold mb-3">Payment Details</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Payment Method</span>
              <span className="font-medium">{subscription?.paymentMethod}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Auto-Renew</span>
              <span className={`font-medium ${subscription?.autoRenew ? 'text-success' : 'text-error'}`}>
                {subscription?.autoRenew ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Transaction ID</span>
              <span className="font-mono text-xs">{subscription?.transactionId}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 bg-card rounded-lg border border-border">
        <h4 className="text-sm font-caption font-semibold mb-3">Premium Benefits</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <Icon name="Check" size={16} color="var(--color-success)" />
            <span className="text-sm text-muted-foreground">Unlimited messaging</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Check" size={16} color="var(--color-success)" />
            <span className="text-sm text-muted-foreground">Advanced search filters</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Check" size={16} color="var(--color-success)" />
            <span className="text-sm text-muted-foreground">Priority profile visibility</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Check" size={16} color="var(--color-success)" />
            <span className="text-sm text-muted-foreground">Read receipts</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Check" size={16} color="var(--color-success)" />
            <span className="text-sm text-muted-foreground">See who viewed your profile</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Check" size={16} color="var(--color-success)" />
            <span className="text-sm text-muted-foreground">Ad-free experience</span>
          </div>
        </div>
      </div>
      <div>
        <h4 className="text-sm font-caption font-semibold mb-3">Payment History</h4>
        <div className="space-y-2">
          {invoices?.map((invoice) => (
            <div
              key={invoice?.id}
              className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 transition-smooth"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-card rounded-lg">
                  <Icon name="FileText" size={20} />
                </div>
                <div>
                  <p className="text-sm font-medium">{invoice?.invoiceNumber}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(invoice?.date)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-mono font-semibold">₹{invoice?.amount?.toLocaleString('en-IN')}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Download"
                  onClick={() => handleDownloadInvoice(invoice?.invoiceNumber)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
        <Button
          variant="default"
          fullWidth
          iconName="CreditCard"
          iconPosition="left"
          onClick={() => navigate('/subscription-payment')}
        >
          Update Payment Method
        </Button>
        <Button
          variant="destructive"
          fullWidth
          iconName="XCircle"
          iconPosition="left"
          onClick={() => setShowCancelModal(true)}
        >
          Cancel Subscription
        </Button>
      </div>
      {showCancelModal && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-background/80">
          <div className="w-full max-w-md bg-card rounded-xl shadow-warm-2xl p-6 animate-scale-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-error/10 flex items-center justify-center">
                <Icon name="AlertTriangle" size={32} color="var(--color-error)" />
              </div>
              <h3 className="text-lg font-heading font-semibold mb-2">Cancel Subscription?</h3>
              <p className="text-sm text-muted-foreground">
                You will lose access to premium features after {formatDate(subscription?.renewalDate)}. You can reactivate anytime.
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="X" size={16} color="var(--color-error)" />
                <span>No more unlimited messaging</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="X" size={16} color="var(--color-error)" />
                <span>Limited search filters</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="X" size={16} color="var(--color-error)" />
                <span>Reduced profile visibility</span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowCancelModal(false)}
              >
                Keep Subscription
              </Button>
              <Button
                variant="destructive"
                fullWidth
                iconName="XCircle"
                iconPosition="left"
                onClick={handleCancelSubscription}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagementSection;